import { useQueryClient } from 'react-query';
import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  createItemsSignatureRequest,
  LearnosityConfig,
  Question,
} from '@learnosityassessment/AssessmentServiceAPI';
import { useGetSignature } from '@learnosityassessment/Hooks/Query';
import { TestWrapper } from './styles';
import '@learnosityassessment/styles.css';

interface LearnosityEventHandler {
  name: string;
  callback: (widgetInstance: any, eventData: any[]) => void;
}

interface TestHarnessProps {
  // eslint-disable-next-line react/no-unused-prop-types
  ref: any;
  userId: string;
  sessionId: string;
  activityId: string;
  renderingType: 'assess' | 'inline';
  type: 'local_practice' | 'submit_practice' | 'feedback';
  name: string;
  state: 'review' | 'initial' | 'resume' | 'preview';
  questionReferences?: Question[];
  activityTemplateId?: string;
  config: LearnosityConfig;
  readyCallback?: (widgetInstance: any) => void;
  errorCallback?: (error: any) => void;
  eventHandlers?: LearnosityEventHandler[];
}

const TestHarness: React.ForwardRefExoticComponent<TestHarnessProps> = forwardRef<
  any,
  TestHarnessProps
>(
  (
    {
      name,
      userId,
      sessionId,
      activityId,
      renderingType,
      type,
      state,
      questionReferences,
      config,
      readyCallback,
      errorCallback,
      eventHandlers,
      activityTemplateId,
    },
    widgetRef,
  ) => {
    // either questionReferences or activityTemplateId must be provided
    if (!questionReferences && !activityTemplateId) {
      throw new Error('Either questionReferences or activityTemplateId must be provided');
    }
    // generate unique instance ID in the case where we need multiple assessment experiences on a page shell
    const stableContainerId = useMemo(() => `assessmentWidget-${sessionId}`, []);
    const [widgetInstance, setWidgetInstance] = useState<any>(undefined);
    const { data: signature } = useGetSignature(
      createItemsSignatureRequest(
        name,
        userId,
        sessionId,
        activityId,
        renderingType,
        type,
        state,
        config,
        questionReferences || undefined,
        activityTemplateId,
      ),
      'items',
      {
        enabled: !widgetInstance && !!(window as any).LearnosityItems,
      },
    );
    // set the ref whenever we change the widgetInstance so the parent component can access it directly
    useImperativeHandle(
      widgetRef,
      () => ({
        widgetInstance,
        getWidgetInstance() {
          return widgetInstance;
        },
      }),
      [widgetInstance],
    );
    const queryClient = useQueryClient();
    useEffect(() => {
      if (Object.keys(signature!).length && !widgetInstance) {
        // call factory for request body
        // initialize items api instance
        const itemsAPI = (window as any).LearnosityItems.init(
          signature,
          {
            readyListener: () => {
              // TODO add base handler for ready event
              readyCallback?.(widgetInstance);
            },
            errorListener: (error: any) => {
              // TODO add base error handler
              queryClient.invalidateQueries(['learnosityService', 'Signature']);
              errorCallback?.(error);
            },
          },
          stableContainerId,
        );
        // set up hooks
        if (eventHandlers) {
          eventHandlers.forEach(({ name, callback }) => {
            itemsAPI.on(name, (...args: any[]) => {
              callback(itemsAPI, args);
            });
          });
        }
        // save item widget instance
        setWidgetInstance(itemsAPI);
      }

      return () => {
        // clear and reset old item instance on unmount
        if (widgetInstance) {
          widgetInstance.reset();
        }
      };
    }, [signature]);

    return <TestWrapper id={stableContainerId} />;
  },
);
TestHarness.displayName = 'TestHarness';
const memoTestHarness = memo(TestHarness);

export { TestHarness, memoTestHarness, TestHarnessProps };
